import { Combobox } from '@base-ui/react/combobox';
import { useCallback, useRef, type RefObject } from 'react';
import { Chevron } from '#icons/Chevron';
import { Label, SkeletonLoader, spacing } from 'component-library';
import { useDropdownContext } from '../../context/DropdownContext';
import { getTopmostSelectedLabels, isFullySelected } from '../../utils/selection/index';
import { styles } from './styles';
import type { DropdownValue, EditableSingleValue, FormatSelectedValues } from '../../types';

type DropdownTriggerProps = {
  formatSelectedValues?: FormatSelectedValues;
  editableSingleValue?: EditableSingleValue;
  disabled?: boolean;
  labelId?: string;
  describedBy?: string;
  customInputRef?: RefObject<HTMLInputElement | null>;
};

const DefaultSelectedValuesFormatter = ({ value }: { value: DropdownValue[] }) => {
  const {
    triggerPlaceholder,
    allSelected,
    allSelectedLabel,
    labelByValue,
    allItems,
    descendantsByValue,
    selectedSet,
    selectedValuesDisplay,
    countSelectedGroups,
    dataTestId,
  } = useDropdownContext();
  const validSelectedValues = value.filter((selectedValue) => labelByValue.has(selectedValue));
  const selectedGroupCount = countSelectedGroups
    ? allItems.filter(
        (item) => item.type === 'group' && isFullySelected(item, descendantsByValue, selectedSet),
      ).length
    : 0;
  const selectedCount = validSelectedValues.length + selectedGroupCount;

  if (validSelectedValues.length === 0) {
    return triggerPlaceholder;
  }

  const getFirstSelectedText = () => {
    const itemLabel = labelByValue.get(validSelectedValues[0]) ?? triggerPlaceholder;

    const allSelectedGroups = allItems.filter(
      (item) => item.type === 'group' && isFullySelected(item, descendantsByValue, selectedSet),
    );

    const firstSelectedGroup =
      allSelectedGroups
        .map((item) => labelByValue.get(item.value))
        .find((groupLabel): groupLabel is string => groupLabel !== undefined) ?? null;

    return firstSelectedGroup || itemLabel;
  };

  const getSelectedListText = () =>
    getTopmostSelectedLabels({ allItems, descendantsByValue, labelByValue, selectedSet }).join(
      ', ',
    );

  const getSelectedText = () => {
    if (allSelected) {
      return allSelectedLabel;
    }

    if (selectedValuesDisplay === 'list') {
      return getSelectedListText();
    }

    return getFirstSelectedText();
  };

  const selectedText = getSelectedText();

  return (
    <div css={styles.DefaultValue}>
      <span
        css={styles.DefaultValueText}
        title={selectedText}
        data-testid={`${dataTestId}-trigger-value`}
      >
        {selectedText}
      </span>
      {selectedCount > 1 && (
        <span css={styles.DefaultValueCount}>
          <Label variant="info">{selectedCount}</Label>
        </span>
      )}
    </div>
  );
};

export function DropdownTrigger({
  formatSelectedValues,
  editableSingleValue,
  disabled = false,
  labelId,
  describedBy,
  customInputRef,
}: DropdownTriggerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { loading, error, required, label } = useDropdownContext();

  const selectedValuesFormatter = useCallback(
    (value: DropdownValue[]) =>
      formatSelectedValues?.(value) ?? <DefaultSelectedValuesFormatter value={value} />,
    [formatSelectedValues],
  );

  if (editableSingleValue && !loading) {
    return (
      <div css={styles.EditableSingleValue({ error })} data-disabled={disabled || undefined}>
        <input
          ref={customInputRef}
          aria-invalid={error || undefined}
          aria-label={editableSingleValue.ariaLabel}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          autoFocus
          css={styles.EditableInput}
          disabled={disabled}
          onChange={(event) => {
            editableSingleValue.onChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              triggerRef.current?.focus();
              triggerRef.current?.click();
            }
          }}
          placeholder={editableSingleValue.placeholder}
          type="text"
          value={editableSingleValue.value}
        />
        <Combobox.Trigger
          ref={triggerRef}
          aria-label={label}
          css={styles.EditableSingleValueButton}
          disabled={disabled}
        >
          <Combobox.Icon css={styles.SelectIcon}>
            <Chevron height="24px" width="24px" />
          </Combobox.Icon>
        </Combobox.Trigger>
      </div>
    );
  }

  return (
    <Combobox.Trigger
      css={styles.Trigger({ error })}
      aria-invalid={error || undefined}
      aria-describedby={describedBy}
      aria-required={required || undefined}
    >
      {!loading && <Combobox.Value>{selectedValuesFormatter}</Combobox.Value>}
      {loading && <SkeletonLoader height={spacing.small} />}
      <Combobox.Icon css={styles.SelectIcon}>
        <Chevron height="24px" width="24px" />
      </Combobox.Icon>
    </Combobox.Trigger>
  );
}
